import { motion } from 'framer-motion';
import { Package2, Star, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePackages, useServices } from '../hooks/useFirestoreServices';
import LoadingSpinner from './LoadingSpinner';
import { useEffect, useState } from 'react';
import { generateReviews, getReviewStats } from '../utils/reviewGenerator';

interface PackageWithServices {
  id: string;
  name: string;
  description: string;
  serviceIds: string[];
  price: number;
  originalPrice?: number;
  discount?: number;
  savings: number;
  image: string;
  images?: string[];
  popular?: boolean;
  featured?: boolean;
  new?: boolean;
  trending?: boolean;
  isActive?: boolean;
  serviceNames?: string[];
}

export default function PackagesSection() {
  const { packages, loading: packagesLoading } = usePackages();
  const { services, loading: servicesLoading } = useServices();
  const [packagesWithServices, setPackagesWithServices] = useState<PackageWithServices[]>([]);
  
  // Map service IDs to service names
  useEffect(() => {
    if (packages.length > 0 && services.length > 0) {
      const packagesData = packages.map(pkg => {
        const serviceNames = pkg.serviceIds?.map(serviceId => {
          const service = services.find(s => s.id === serviceId);
          return service?.name || '';
        }).filter(name => name !== '') || [];
        
        return {
          ...pkg,
          serviceNames
        };
      });
      setPackagesWithServices(packagesData);
    }
  }, [packages, services]);
  
  // Show only active packages
  const displayPackages = packagesWithServices
    .filter(pkg => pkg.isActive !== false)
    .slice(0, 8);

  if (packagesLoading || servicesLoading) {
    return (
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center min-h-[400px]">
            <LoadingSpinner />
          </div>
        </div>
      </section>
    );
  }

  if (displayPackages.length === 0) {
    return null; // Don't show section if no packages
  }

  return (
    <section className="py-24 bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-primary-300/20 to-purple-300/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-secondary-300/20 to-pink-300/20 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Enhanced Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-6 py-2.5 rounded-full text-sm font-semibold mb-6 shadow-lg shadow-primary-500/30">
            <Package2 className="h-5 w-5 mr-2" />
            Service Packages
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Save More with 
            <span className="block mt-2 bg-gradient-to-r from-primary-600 via-secondary-600 to-purple-600 bg-clip-text text-transparent">
              Premium Package Deals
            </span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Get multiple services bundled together at amazing prices with our exclusive package offers
          </p>
        </motion.div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 mb-12">
          {displayPackages.map((pkg, index) => {
            const discount = pkg.discount || (pkg.originalPrice && pkg.price 
              ? parseFloat((((pkg.originalPrice - pkg.price) / pkg.originalPrice) * 100).toFixed(1))
              : 0);

            return (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group"
              >
                <Link to={`/packages/${pkg.id}`} className="block h-full">
                  <article className="h-full rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 relative bg-white border-2 border-transparent hover:border-primary-200">
                    {/* Premium Gradient Overlay for Popular Packages */}
                    {pkg.popular && (
                      <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 via-purple-500/5 to-pink-500/5 pointer-events-none"></div>
                    )}
                    
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={pkg.image}
                        alt={pkg.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>

                      {/* Top Badges */}
                      <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                        <span className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-lg backdrop-blur-sm">
                          Package
                        </span>
                        {pkg.popular && (
                          <span className="bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-lg animate-pulse">
                            ⭐ Popular
                          </span>
                        )}
                        {pkg.new && (
                          <span className="bg-gradient-to-r from-green-400 to-emerald-500 text-white text-xs px-3 py-1.5 rounded-full font-bold shadow-lg">
                            🎉 New
                          </span>
                        )}
                      </div>

                      {/* Discount Badge */}
                      {discount > 0 && (
                        <div className="absolute top-4 right-4">
                          <div className="relative">
                            <div className="absolute inset-0 bg-red-500 blur-md opacity-70"></div>
                            <div className="relative bg-gradient-to-r from-red-500 to-rose-600 text-white px-4 py-2 rounded-2xl text-sm font-bold shadow-lg">
                              {discount}% OFF
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="p-6">
                      <div className="mb-4">
                        <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-primary-600 transition-colors leading-tight line-clamp-2">
                          {pkg.name}
                        </h3>
                        
                        {/* Rating */}
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-yellow-400 fill-current" />
                            {(() => {
                              const reviews = generateReviews(pkg.id);
                              const stats = getReviewStats(reviews);
                              return (
                                <>
                                  <span className="font-semibold text-gray-900 text-sm">{stats.avgRating.toFixed(1)}</span>
                                  <span className="text-gray-500 text-sm">({stats.totalReviews})</span>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                        
                        <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed">{pkg.description}</p>
                      </div>

                      {/* Display Service Names */}
                      {pkg.serviceNames && pkg.serviceNames.length > 0 && (
                        <div className="mb-5 bg-gradient-to-br from-primary-50 to-purple-50 rounded-xl p-3">
                          <p className="text-xs text-primary-700 font-bold mb-2 uppercase tracking-wide">Includes:</p>
                          <div className="space-y-1.5">
                            {pkg.serviceNames.slice(0, 3).map((serviceName, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                                <span className="text-xs text-gray-700 line-clamp-1 font-medium">{serviceName}</span>
                              </div>
                            ))}
                            {pkg.serviceNames.length > 3 && (
                              <p className="text-xs text-primary-600 font-bold mt-2 flex items-center gap-1">
                                <span className="inline-block w-1.5 h-1.5 bg-primary-600 rounded-full"></span>
                                +{pkg.serviceNames.length - 3} more services
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Pricing */}
                      <div className="flex items-baseline gap-3 mb-5 pb-5 border-b-2 border-gray-100">
                        <span className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                          ₹{pkg.price}
                        </span>
                        {pkg.originalPrice && pkg.originalPrice > pkg.price && (
                          <span className="text-gray-400 line-through text-base">₹{pkg.originalPrice}</span>
                        )}
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                          <span className="text-sm text-green-600 font-bold">Save ₹{Number(pkg.savings || 0).toLocaleString('en-IN')}</span>
                        </div>
                        <div className="flex items-center gap-2 text-primary-600 font-semibold group-hover:gap-3 transition-all">
                          <span className="text-sm">View Details</span>
                          <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>

                    {/* Premium Shine Effect */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                    </div>
                  </article>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* View All Button */}
        {packages.length > 8 && (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link
              to="/packages"
              className="inline-flex items-center bg-gradient-to-r from-primary-600 via-secondary-600 to-purple-600 text-white px-10 py-4 rounded-2xl font-bold text-lg hover:shadow-2xl hover:shadow-primary-500/50 transition-all duration-300 group hover:scale-105"
            >
              <span>View All Packages</span>
              <ArrowRight className="ml-3 h-6 w-6 group-hover:translate-x-2 transition-transform" />
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
}
